import React, {useEffect, useMemo, useRef, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {useStore} from 'effector-react';
import {useForm, Controller, useFieldArray, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as HelperIcon} from 'assets/svg/helper.svg';
import 'core/yupCustomValidations';

import {XlsxFileForm, FileFormPropsCallback} from 'components/common/XlsxFileForm';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {HtmlText} from 'components/ui-kit/HTMLText';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Tooltip} from 'components/ui-kit/Tooltip';
import {ROUTE} from 'core/routes';
import {
    stepChanged,
    productsChanged,
    products$,
    file$,
    fileChanged,
    fetchGtinInfoEvent,
} from 'models/orders/create';
import {balance$} from 'models/profile/billing';
import {colors} from 'theme';
import {OrderProductsData} from 'types';

import {
    NavigationButtonsContainer,
    FormContainer,
    Fieldset,
    FormPartTitle,
    FormField,
    AvailableCodesContainer,
    TitleWrapper,
    FormPartDescription,
    MedicalProductField,
    MedicalProductFields,
    MedicalProductFieldError,
    SeparationLine,
    SeparationLineText,
    Wrapper,
} from '../styled';

import {ProductCode} from './ProductCode';
import {ProductSingleItem} from './productSingleItem';
import {
    getMaxMedicationExpirationDate,
    productsSchema,
    productsSchemaNew,
    QuantityValidationContext,
} from './schemas/secondStep';

const additionalStyles = `
    text-align: left;
    max-width: 250px;
    border-radius: 8px;
    opacity: 1 !important;
    `;

const combineDateWithTime = (date: Date, timeSource: string) => {
    const result = new Date(date);
    const time = timeSource ? new Date(timeSource) : new Date();
    result.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
    return result;
};

export const OrderProducts = () => {
    const {t, i18n} = useTranslation();
    const products = useStore(products$);
    const balance = useStore(balance$);
    const file = useStore(file$);

    const quantityValidationContextRef = React.useRef<QuantityValidationContext>({
        balance: 0,
        codesLeft: 0,
    });

    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const tooltipTriggerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isTooltipOpen && tooltipTriggerRef.current) {
            ReactTooltip.show(tooltipTriggerRef.current);
        }
    }, [isTooltipOpen]);

    const {
        control,
        handleSubmit,
        formState,
        reset,
        setValue,
        getValues,
        trigger,
        clearErrors,
    } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        shouldUnregister: false,
        resolver: yupResolver(productsSchemaNew),
        context: quantityValidationContextRef.current,
        defaultValues: {
            gtin: '',
            quantity: '',
            manufacturingDate: '',
            expirationDate: '',
            batchNumber: '',
            products,
        },
    });

    const {fields, append, remove} = useFieldArray({
        control,
        name: 'products',
    });
    const watchedProducts = useWatch({
        control,
        name: 'products',
    });
    const selectedProduct = useWatch({
        control,
        name: 'gtin',
    }) as any;
    const selectedQuantity = useWatch({
        control,
        name: 'quantity',
    });
    const selectedManufacturingDate = useWatch({
        control,
        name: 'manufacturingDate',
    }) as string;
    const selectedExpirationDate = useWatch({
        control,
        name: 'expirationDate',
    }) as string;
    const selectedBatchNumber = useWatch({
        control,
        name: 'batchNumber',
    }) as string;
    const selectedProductGtin = selectedProduct?.value;
    const isMedicationSelected = selectedProduct?.meta?.productGroupCode === 'MEDICATIONS';

    useEffect(() => {
        setValue('manufacturingDate', isMedicationSelected ? new Date().toISOString() : '', {
            shouldValidate: false,
        });
        setValue('expirationDate', '', {shouldValidate: false});
        setValue('batchNumber', '', {shouldValidate: false});
        clearErrors(['manufacturingDate', 'expirationDate', 'batchNumber']);
    }, [clearErrors, isMedicationSelected, selectedProductGtin, setValue]);

    const totalQuantity = useMemo(() => {
        if (!(watchedProducts as OrderProductsData[])?.length) return 0;
        const total = (watchedProducts as OrderProductsData[])?.reduce(
            (sum, item) => sum + (Number(item.quantity) || 0),
            0,
        );

        return total;
    }, [watchedProducts]);

    const codesLeft = useMemo(
        () => (balance - Math.abs(totalQuantity) < 0 ? 0 : balance - Math.abs(totalQuantity)),
        [balance, totalQuantity],
    );

    useEffect(() => {
        quantityValidationContextRef.current.balance = balance;
        quantityValidationContextRef.current.codesLeft = codesLeft;
    }, [balance, codesLeft]);

    const productGroupCodeList = useMemo(() => {
        if (!fields.length) return [];
        const productGroupCodeList = new Set(
            fields.map((product) => product.productGroupCode).filter(Boolean),
        );

        return Array.from(productGroupCodeList);
    }, [fields]);
    const selectedProductGroupCode = selectedProduct?.meta?.productGroupCode;
    const hasProductGroupConflict =
        !!selectedProductGroupCode &&
        productGroupCodeList.length > 0 &&
        !productGroupCodeList.includes(selectedProductGroupCode);
    const packageTypeList = useMemo(() => {
        if (!fields.length) return [];
        return Array.from(new Set(fields.map((product) => product.type).filter(Boolean)));
    }, [fields]);
    const selectedPackageType = selectedProduct?.meta?.type;
    const hasPackageTypeConflict =
        !!selectedPackageType &&
        packageTypeList.length > 0 &&
        !packageTypeList.includes(selectedPackageType);
    const gtinList = useMemo(() => fields.map((product) => product.gtin).filter(Boolean), [fields]);
    const hasDuplicateGtins = new Set(gtinList).size !== gtinList.length;
    const hasSelectedGtinDuplicate =
        !!selectedProductGtin && gtinList.includes(selectedProductGtin);

    const exampleLink = useMemo(() => {
        const pathByLanguage: Record<string, string> = {
            en: '/examples/order_example_en.xlsx',
            fr: '/examples/order_example_fr.xlsx',
        };

        return pathByLanguage[i18n.language] || '/examples/order_example.xlsx';
    }, [i18n.language]);

    const clearInputFields = () => {
        setValue('gtin', null, {shouldValidate: false});
        setValue('quantity', '', {shouldValidate: false});
        setValue('manufacturingDate', '', {shouldValidate: false});
        setValue('expirationDate', '', {shouldValidate: false});
        setValue('batchNumber', '', {shouldValidate: false});
    };

    const allowToAddProduct = useMemo(() => {
        if (!selectedProduct || !Number(selectedQuantity)) return false;
        if (
            isMedicationSelected &&
            (!selectedManufacturingDate || !selectedExpirationDate || !selectedBatchNumber)
        ) {
            return false;
        }
        if (balance === 0 || balance < Math.abs(totalQuantity)) return false;
        if (
            productGroupCodeList.length > 1 ||
            hasProductGroupConflict ||
            packageTypeList.length > 1 ||
            hasPackageTypeConflict ||
            hasDuplicateGtins ||
            hasSelectedGtinDuplicate
        )
            return false;
        if (fields.length >= 10) return false;
        if (formState.errors.gtin || formState.errors.quantity) return false;
        return !(
            isMedicationSelected &&
            (formState.errors.manufacturingDate ||
                formState.errors.expirationDate ||
                formState.errors.batchNumber)
        );
    }, [
        balance,
        totalQuantity,
        productGroupCodeList,
        hasProductGroupConflict,
        packageTypeList,
        hasPackageTypeConflict,
        hasDuplicateGtins,
        hasSelectedGtinDuplicate,
        fields,
        formState.errors.gtin,
        formState.errors.quantity,
        formState.errors.manufacturingDate,
        formState.errors.expirationDate,
        formState.errors.batchNumber,
        selectedProduct,
        selectedQuantity,
        isMedicationSelected,
        selectedManufacturingDate,
        selectedExpirationDate,
        selectedBatchNumber,
    ]);

    const onHandleChanged = (data: any) => {
        const isMedication = data.gtin.meta.productGroupCode === 'MEDICATIONS';

        append({
            gtin: data.gtin.value,
            quantity: data.quantity,
            productName: data.gtin.meta.productName,
            productGroupCode: data.gtin.meta.productGroupCode,
            type: data.gtin.meta.type,
            productVersionId: data.gtin.meta.productVersionId ?? '',
            ...(isMedication && {
                manufacturingDate: data.manufacturingDate,
                expirationDate: data.expirationDate,
                batchNumber: data.batchNumber.toUpperCase(),
            }),
        });
        clearInputFields();
    };

    const handleSteps = async (step: number) => {
        const formValues = (getValues('products') ?? []) as OrderProductsData[];
        const formProducts = fields.map((field, index) => ({
            ...field,
            ...formValues[index],
        }));

        if (step === 2) {
            const groupCodes = new Set(
                formProducts.map(({productGroupCode}) => productGroupCode).filter(Boolean),
            );
            const packageTypes = new Set(formProducts.map(({type}) => type).filter(Boolean));
            const gtins = formProducts.map(({gtin}) => gtin).filter(Boolean);
            if (
                groupCodes.size > 1 ||
                packageTypes.size > 1 ||
                new Set(gtins).size !== gtins.length
            )
                return;

            if (!(await trigger('products'))) return;
        }

        const payload: OrderProductsData[] = formProducts.map((field) => ({
            gtin: field.gtin,
            quantity: field.quantity,
            productName: field.productName,
            productGroupCode: field.productGroupCode,
            type: field.type,
            productVersionId: field.productVersionId ?? '',
            ...(field.productGroupCode === 'MEDICATIONS' && {
                manufacturingDate: field.manufacturingDate,
                expirationDate: field.expirationDate,
                batchNumber: field.batchNumber?.toUpperCase(),
            }),
        }));
        productsChanged(payload);
        stepChanged(step);
    };
    const handleFileChanged = ({data, file: newFile, isValid}: FileFormPropsCallback) => {
        productsChanged(isValid ? data : []);
        fileChanged(newFile);
        if (isValid) {
            stepChanged(2);
            fetchGtinInfoEvent();
        }

        // Reset react-hook-form, then file was deleted
        if (file && !newFile) {
            reset({
                gtin: '',
                quantity: '',
                products: [],
            });
        }
    };

    return (
        <FormContainer>
            <Grid container spacing={3}>
                <Grid item md={7} xs={12}>
                    <TitleWrapper>
                        <FormPartTitle>
                            {t('pages.orders.form.uploadProductsListTitle')}
                        </FormPartTitle>
                        <FormPartDescription>
                            {t('pages.orders.form.uploadProductsListDescription')}
                        </FormPartDescription>
                    </TitleWrapper>
                </Grid>
                <Grid item md={5} xs={12}>
                    <XlsxFileForm
                        updatedDesign
                        horizontal
                        title={t('pages.orders.form.uploadProductsListTitle')}
                        onChange={handleFileChanged}
                        scheme={productsSchema}
                        assessors={{
                            productName: 'A',
                            gtin: 'B',
                            quantity: 'C',
                            type: 'D',
                        }}
                        localizedFields={{type: 'filePackageTypes'}}
                        initialValue={file}
                        linkExample={exampleLink}
                        // linkExampleAction={(path) => {
                        //     downloadXlsx({
                        //         path,
                        //         localePath: 'examples.order',
                        //     });
                        // }}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <SeparationLine>
                    <SeparationLineText>{t('pages.orders.form.or')}</SeparationLineText>
                </SeparationLine>
            </Grid>
            {!file && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <form onSubmit={handleSubmit(onHandleChanged)}>
                            <Fieldset>
                                <FormField>
                                    <Grid container spacing={3}>
                                        <Grid item md={6} xs={12}>
                                            <ProductCode
                                                control={control}
                                                defaultValue={''}
                                                name={`gtin`}
                                                getValues={getValues}
                                            />
                                            {formState.errors.gtin && (
                                                <span style={{fontSize: 12, color: 'red'}}>
                                                    {t(formState.errors.gtin.message as string)}
                                                </span>
                                            )}
                                            <Wrapper>
                                                {t('pages.orders.form.gtinNotFoundTitle')}
                                                <div
                                                    ref={tooltipTriggerRef}
                                                    data-tip
                                                    data-for="saveTooltip"
                                                    onMouseEnter={() => setIsTooltipOpen(true)}
                                                >
                                                    <Icon
                                                        source={HelperIcon}
                                                        width={20}
                                                        height={20}
                                                        fillColor="transparent"
                                                        style={{color: colors.brandPrimary}}
                                                    />
                                                </div>
                                                {isTooltipOpen && (
                                                    <Tooltip
                                                        id="saveTooltip"
                                                        place={'top'}
                                                        clickable
                                                        delayHide={1000}
                                                        backgroundColor="white"
                                                        textColor="#111111"
                                                        additionalStyles={
                                                            additionalStyles +
                                                            'box-shadow: 0px 0px 6px rgba(116, 116, 116, 0.3); pointer-events: auto;'
                                                        }
                                                    >
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 8,
                                                                paddingRight: 16,
                                                                position: 'relative',
                                                            }}
                                                        >
                                                            <button
                                                                type="button"
                                                                aria-label="Close"
                                                                onClick={() =>
                                                                    setIsTooltipOpen(false)
                                                                }
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: -4,
                                                                    right: -8,
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    padding: 4,
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <Icon
                                                                    source={CrossIcon}
                                                                    width={10}
                                                                    height={10}
                                                                />
                                                            </button>
                                                            <HtmlText
                                                                Tag="span"
                                                                __html={t(
                                                                    'pages.orders.form.gtinNotFound',
                                                                )}
                                                            />
                                                            <Link
                                                                to={ROUTE.REGISTRIES_GTIN}
                                                                style={{
                                                                    color: colors.brandPrimary,
                                                                    textDecoration: 'underline',
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {t(
                                                                    'pages.orders.form.gtinNotFoundLink',
                                                                )}
                                                            </Link>
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </Wrapper>
                                        </Grid>
                                        <Grid item md={6} xs={12}>
                                            <Controller
                                                control={control}
                                                name={`quantity`}
                                                defaultValue={''}
                                                render={(props, {invalid}) => (
                                                    <Input
                                                        {...props}
                                                        label={t('pages.orders.form.quantity')}
                                                        placeholder={t(
                                                            'pages.orders.form.quantity',
                                                        )}
                                                        hasError={invalid}
                                                    />
                                                )}
                                            />
                                            {formState.errors.quantity && (
                                                <span style={{fontSize: 12, color: 'red'}}>
                                                    {t(formState.errors.quantity.message as string)}
                                                </span>
                                            )}
                                        </Grid>
                                    </Grid>
                                    {isMedicationSelected && (
                                        <MedicalProductFields>
                                            <MedicalProductField>
                                                <Controller
                                                    control={control}
                                                    name="manufacturingDate"
                                                    defaultValue=""
                                                    render={({value}) => (
                                                        <DatePicker
                                                            disabled
                                                            isRequired
                                                            label={t(
                                                                'pages.orders.form.manufacturingDate',
                                                            )}
                                                            value={value ? new Date(value) : null}
                                                        />
                                                    )}
                                                />
                                                {formState.errors.manufacturingDate && (
                                                    <MedicalProductFieldError>
                                                        {t(
                                                            formState.errors.manufacturingDate
                                                                .message as string,
                                                        )}
                                                    </MedicalProductFieldError>
                                                )}
                                            </MedicalProductField>
                                            <MedicalProductField>
                                                <Controller
                                                    control={control}
                                                    name="expirationDate"
                                                    defaultValue=""
                                                    render={({value, onChange}, {invalid}) => (
                                                        <DatePicker
                                                            isRequired
                                                            label={t(
                                                                'pages.orders.form.expirationDate',
                                                            )}
                                                            minDate={new Date()}
                                                            maxDate={getMaxMedicationExpirationDate()}
                                                            value={value ? new Date(value) : null}
                                                            hasError={invalid}
                                                            onChange={(date) =>
                                                                onChange(
                                                                    date
                                                                        ? combineDateWithTime(
                                                                              date,
                                                                              selectedManufacturingDate,
                                                                          ).toISOString()
                                                                        : '',
                                                                )
                                                            }
                                                        />
                                                    )}
                                                />
                                                {formState.errors.expirationDate && (
                                                    <MedicalProductFieldError>
                                                        {t(
                                                            formState.errors.expirationDate
                                                                .message as string,
                                                        )}
                                                    </MedicalProductFieldError>
                                                )}
                                            </MedicalProductField>
                                            <MedicalProductField>
                                                <Controller
                                                    control={control}
                                                    name="batchNumber"
                                                    defaultValue=""
                                                    render={(props, {invalid}) => (
                                                        <Input
                                                            {...props}
                                                            maxLength={20}
                                                            autoCapitalize="characters"
                                                            isRequired
                                                            label={t(
                                                                'pages.orders.form.batchNumber',
                                                            )}
                                                            placeholder={t(
                                                                'pages.orders.form.batchNumber',
                                                            )}
                                                            hasError={invalid}
                                                            onChange={(event) =>
                                                                props.onChange(
                                                                    event.target.value.toUpperCase(),
                                                                )
                                                            }
                                                        />
                                                    )}
                                                />
                                                {formState.errors.batchNumber && (
                                                    <MedicalProductFieldError>
                                                        {t(
                                                            formState.errors.batchNumber
                                                                .message as string,
                                                        )}
                                                    </MedicalProductFieldError>
                                                )}
                                            </MedicalProductField>
                                        </MedicalProductFields>
                                    )}
                                </FormField>
                            </Fieldset>
                            <Button
                                type="submit"
                                style={{width: 200}}
                                variant={ButtonVariant.Normal}
                                disabled={!allowToAddProduct}
                            >
                                + {t('pages.orders.form.add')}
                            </Button>
                            <AvailableCodesContainer
                                noAvailable={balance === 0 || balance < Math.abs(totalQuantity)}
                            >
                                {t('components.form.codesAvailableLeft')}: {codesLeft}
                            </AvailableCodesContainer>
                            {(productGroupCodeList.length > 1 || hasProductGroupConflict) && (
                                <AvailableCodesContainer noAvailable>
                                    {t('components.form.onlyOneProductGroup')}
                                </AvailableCodesContainer>
                            )}
                            {(packageTypeList.length > 1 || hasPackageTypeConflict) && (
                                <AvailableCodesContainer noAvailable>
                                    {t('components.form.onlyOnePackageType')}
                                </AvailableCodesContainer>
                            )}
                            {(hasDuplicateGtins || hasSelectedGtinDuplicate) && (
                                <AvailableCodesContainer noAvailable>
                                    {t('components.form.duplicateGtin')}
                                </AvailableCodesContainer>
                            )}
                            {fields.length >= 10 && (
                                <AvailableCodesContainer noAvailable>
                                    {t('pages.orders.form.maxProductsPerOrder')}
                                </AvailableCodesContainer>
                            )}
                        </form>
                    </Grid>
                </Grid>
            )}
            {fields?.length > 0 &&
                fields?.map((field, index) => (
                    <ProductSingleItem
                        key={field.id}
                        name={field.productName}
                        gtin={field.gtin}
                        quantity={field.quantity}
                        productGroupCode={field.productGroupCode}
                        manufacturingDate={field.manufacturingDate}
                        expirationDate={field.expirationDate}
                        batchNumber={field.batchNumber}
                        onDelete={() => remove(index)}
                    />
                ))}
            <NavigationButtonsContainer>
                <Button
                    style={{width: 'auto'}}
                    variant={ButtonVariant.Secondary}
                    onClick={() => handleSteps(0)}
                >
                    {t('components.form.prevStep')}
                </Button>
                <Button
                    style={{width: 'auto'}}
                    variant={ButtonVariant.Normal}
                    type="submit"
                    disabled={
                        productGroupCodeList.length > 1 ||
                        packageTypeList.length > 1 ||
                        hasDuplicateGtins ||
                        totalQuantity > balance ||
                        fields.length === 0 ||
                        (!!file && !products.length)
                    }
                    onClick={() => handleSteps(2)}
                >
                    {t('components.form.nextStep')}
                </Button>
            </NavigationButtonsContainer>
        </FormContainer>
    );
};
